import React, { useState, useEffect } from "react";
import {
  Popover,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Button,
  Box,
  Typography,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DateRangePicker from "../../Sections/Common/DateRangePicker";
import "./DataTable.css";

const FilterDrawer = ({
  open,
  anchorEl,
  onClose,
  filters = [],
  filterValues = {},
  onApply,
  onReset
}) => {
  const [draftValues, setDraftValues] = useState({});

  const sharedInputSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 42,
      borderRadius: "10px",
      fontSize: "13.5px",
      color: "#0f172a",
      backgroundColor: "#ffffff",
      transition: "border-color 0.16s ease, box-shadow 0.16s ease",
      "& fieldset": {
        borderColor: "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: "#cbd5e1",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 3px rgba(34, 65, 158, 0.12)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#22419e",
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      padding: "10px 14px",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#94a3b8",
      opacity: 1,
    },
  };

  const sharedSelectSx = {
    ...sharedInputSx,
    "& .MuiSelect-select": {
      padding: "10px 14px !important",
      textAlign: "start",
      display: "flex",
      alignItems: "center",
      minHeight: "20px",
    },
    "& .MuiSelect-icon": {
      color: "#94a3b8",
      right: 12,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 12px 28px -8px rgba(15, 23, 42, 0.14)",
        padding: "6px",
        "& .MuiMenu-list": {
          padding: 0,
        },
        "& .MuiMenuItem-root": {
          minHeight: 40,
          margin: "2px 0",
          borderRadius: "8px",
          fontSize: "13.5px",
          color: "#334155",
        },
        "& .MuiMenuItem-root:hover": {
          backgroundColor: "#f5f7fb",
        },
        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor: "#eef2ff",
          color: "#22419e",
          fontWeight: 600,
        },
        "& .MuiMenuItem-root.Mui-selected:hover": {
          backgroundColor: "#e4e9fb",
        },
      },
    },
  };

  useEffect(() => {
    if (open) {
      setDraftValues(filterValues || {});
    }
  }, [open, filterValues]);

  const handleChange = (name, value) => {
    setDraftValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApply = () => {
    onApply(draftValues);
    onClose();
  };

  const handleReset = () => {
    const cleared = {};
    filters.forEach((f) => {
      if (f.type === "multiSelect") {
        cleared[f.name] = [];
      } else if (f.type === "dateRange") {
        cleared[f.name] = null;
      } else {
        cleared[f.name] = f.defaultValue !== undefined ? f.defaultValue : "all";
      }
    });
    setDraftValues(cleared);
    onReset(cleared);
    onClose();
  };

  const renderFilterInput = (filter) => {
    const value = draftValues[filter.name];

    switch (filter.type) {
      case "search":
        return (
          <TextField
            fullWidth
            size="small"
            value={value || ""}
            placeholder={filter.placeholder || "Search..."}
            onChange={(e) => handleChange(filter.name, e.target.value)}
            sx={sharedInputSx}
          />
        );

      case "dateRange":
        return (
          <div className="filter-date-range">
            <DateRangePicker
              selectedRange={value || null}
              onRangeChange={(range) => handleChange(filter.name, range)}
              placeholder={filter.placeholder || "Select date range"}
            />
          </div>
        );

      case "multiSelect":
        return (
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={value || []}
              onChange={(e) => handleChange(filter.name, e.target.value)}
              input={<OutlinedInput />}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return (
                    <em
                      style={{
                        color: "#94a3b8",
                        fontSize: "13.5px",
                        fontStyle: "normal",
                      }}
                    >
                      {filter.placeholder || "Select Multiple"}
                    </em>
                  );
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {selected.map((val) => {
                      const opt = filter.options?.find((o) => o.value === val);
                      return (
                        <Chip
                          key={val}
                          label={opt?.label || val}
                          size="small"
                          sx={{
                            height: 26,
                            borderRadius: "999px",
                            backgroundColor: "#eef2ff",
                            color: "#22419e",
                            fontWeight: 600,
                            "& .MuiChip-label": {
                              px: 1.25,
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                );
              }}
              sx={sharedSelectSx}
            >
              {filter.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "select":
      case "status":
      case "portal":
      case "tenant":
      default:
        return (
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={
                value !== undefined
                  ? value
                  : filter.defaultValue !== undefined
                    ? filter.defaultValue
                    : "all"
              }
              onChange={(e) => handleChange(filter.name, e.target.value)}
              input={<OutlinedInput />}
              MenuProps={menuProps}
              sx={sharedSelectSx}
            >
              {filter.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          className: "filter-popover-paper",
        },
      }}
    >
      <Box className="filter-popover-content">
        <Box className="filter-popover-header">
          <Typography component="h3" className="filter-popover-title">
            Filters
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            className="filter-popover-close"
            aria-label="Close filters"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className="filter-popover-body">
          {filters.map((filter) => (
            <Box key={filter.name} className="filter-popover-field">
              <Typography component="label" className="filter-drawer-label">
                {filter.label}
              </Typography>
              {renderFilterInput(filter)}
            </Box>
          ))}
        </Box>

        <Box className="filter-popover-footer">
          <Button className="filter-btn-reset" onClick={handleReset} size="small">
            Reset
          </Button>
          <Button className="filter-btn-apply" onClick={handleApply} size="small">
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default FilterDrawer;
